/**
 * Asset Type Resolution Utilities
 * 
 * Handles icon and color resolution for assets with fallback from subtype to parent type.
 * 
 * Design requirements:
 * - Map markers use PARENT type icon/color (per Mark: "on the main map. We're just doing equipment")
 * - Reports and detail surfaces use SUBTYPE icon/color when set, falling back to parent
 * - Subtypes can optionally specify their own icon and color, but it's not required
 */

/**
 * Gets the effective icon name for an asset, considering subtype override.
 * Used in reports and detail views where subtype-level iconography matters.
 * 
 * @param {Object} asset - The asset object
 * @param {Object} [options] - Resolution options
 * @param {boolean} [options.forceParent=false] - If true, always use parent type icon (for map)
 * @returns {string} The icon name to use
 */
export function getEffectiveIconName(asset, { forceParent = false } = {}) {
  if (!asset) return 'Mat_Marker';
  
  // For map display, always use parent type icon
  if (forceParent) {
    return asset.type?.iconName || 'Mat_Marker';
  }
  
  // For detail views: subtype icon takes precedence if set
  const subtypeIcon = asset.subType?.iconName;
  if (subtypeIcon) {
    return subtypeIcon;
  }
  
  // Fall back to parent type icon
  return asset.type?.iconName || 'Mat_Marker';
}

/**
 * Gets the effective color for an asset, considering subtype override.
 * Used in reports and detail views where subtype-level differentiation matters.
 * 
 * @param {Object} asset - The asset object
 * @param {Object} [options] - Resolution options
 * @param {boolean} [options.forceParent=false] - If true, always use parent type color (for map)
 * @param {string} [options.defaultColor='#EC4F36'] - Default color if none found
 * @returns {string} The color hex value to use
 */
export function getEffectiveColor(asset, { forceParent = false, defaultColor = '#EC4F36' } = {}) {
  if (!asset) return defaultColor;
  
  // For map display, always use parent type color
  if (forceParent) {
    return asset.type?.color || defaultColor;
  }
  
  // For detail views: subtype color takes precedence if set
  const subtypeColor = asset.subType?.color;
  if (subtypeColor) {
    return subtypeColor;
  }
  
  // Fall back to parent type color
  return asset.type?.color || defaultColor;
}

/**
 * Gets effective icon and color for an asset.
 * Convenience function that returns both values.
 * 
 * @param {Object} asset - The asset object
 * @param {Object} [options] - Resolution options
 * @returns {{ iconName: string, color: string }}
 */
export function getEffectiveIconAndColor(asset, options = {}) {
  return {
    iconName: getEffectiveIconName(asset, options),
    color: getEffectiveColor(asset, options),
  };
}

/**
 * Gets the effective icon name for a subtype with parent type fallback.
 * Used when displaying subtypes in configuration or reports.
 * 
 * @param {Object} subtype - The subtype object
 * @param {Object} parentType - The parent type object
 * @returns {string} The icon name to use
 */
export function getSubtypeIconWithFallback(subtype, parentType) {
  if (subtype?.iconName) {
    return subtype.iconName;
  }
  return parentType?.iconName || 'Mat_Marker';
}

/**
 * Gets the effective color for a subtype with parent type fallback.
 * Used when displaying subtypes in configuration or reports.
 * 
 * @param {Object} subtype - The subtype object
 * @param {Object} parentType - The parent type object
 * @param {string} [defaultColor='#EC4F36'] - Default color if none found
 * @returns {string} The color hex value to use
 */
export function getSubtypeColorWithFallback(subtype, parentType, defaultColor = '#EC4F36') {
  if (subtype?.color) {
    return subtype.color;
  }
  return parentType?.color || defaultColor;
}

/**
 * Checks if a subtype has its own icon (not inherited from parent)
 * 
 * @param {Object} subtype - The subtype object
 * @returns {boolean}
 */
export function subtypeHasOwnIcon(subtype) {
  return !!subtype?.iconName;
}

/**
 * Checks if a subtype has its own color (not inherited from parent)
 * 
 * @param {Object} subtype - The subtype object
 * @returns {boolean}
 */
export function subtypeHasOwnColor(subtype) {
  return !!subtype?.color;
}
